import { Form } from "@lifesg/react-design-system/form";
import { FormInputProps } from "@lifesg/react-design-system/form";
import { AddonProps } from "@lifesg/react-design-system/input-group";
import * as Icons from "@lifesg/react-icons";
import React, { HTMLInputTypeAttribute, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import * as Yup from "yup";
import { IGenericFieldProps } from "..";
import { TestHelper, filterSchemaProps } from "../../../utils";
import { useValidationConfig } from "../../../utils/hooks";
import { ERROR_MESSAGES } from "../../shared";
import { Warning } from "../../shared/warning";
import { IEmailFieldSchema, INumericFieldSchema, INumericFieldValidationRule, ITextFieldSchema } from "./types";

export const TextField = (props: IGenericFieldProps<ITextFieldSchema | IEmailFieldSchema | INumericFieldSchema>) => {
	// ================================================
	// CONST, STATE, REFS
	// ================================================
	const { error, formattedLabel, id, onBlur, onChange, value, schema, warning } = props;
	const {
		commonSchema: { customOptions, uiType, validation },
		customSchema: { inputMode, ...inputProps },
	} = filterSchemaProps(schema);

	const [stateValue, setStateValue] = useState<string | number>(value ?? "");
	const [derivedAttributes, setDerivedAttributes] = useState<FormInputProps>({});
	const { setFieldValidationConfig } = useValidationConfig();

	const ref = useRef<HTMLInputElement>(null);
	const caret = useRef<number>(0);

	// ================================================
	// EFFECTS
	// ================================================
	useEffect(() => {
		switch (uiType) {
			case "numeric-field": {
				const decimalsRule = (validation as INumericFieldValidationRule[])?.find((rule) => "decimals" in rule);
				let yupSchema: Yup.NumberSchema = Yup.number();
				if (decimalsRule?.decimals !== undefined) {
					yupSchema = yupSchema.test(
						"decimals",
						decimalsRule.errorMessage || ERROR_MESSAGES.NUMERIC.INVALID_DECIMALS(decimalsRule.decimals),
						(value) => {
							if (value === undefined || value === null) return true;
							const decimalStr = String(value).split(".")[1];
							return !decimalStr || decimalStr.length <= decimalsRule.decimals;
						}
					);
				}
				setFieldValidationConfig(id, yupSchema, validation);
				const minRule = validation?.find((rule) => "min" in rule);
				const maxRule = validation?.find((rule) => "max" in rule);
				const attributes = { ...derivedAttributes };
				if (minRule?.min > 0) {
					attributes.min = minRule.min;
				}
				if (maxRule?.max > 0) {
					attributes.max = maxRule.max;
				}
				setDerivedAttributes(attributes);
				break;
			}
			case "email-field": {
				const emailRule = validation?.find((rule) => rule.email);
				setFieldValidationConfig(
					id,
					Yup.string().email(emailRule?.errorMessage || ERROR_MESSAGES.EMAIL.INVALID),
					validation
				);

				const maxRule = validation?.find((rule) => "max" in rule);
				const lengthRule = validation?.find((rule) => "length" in rule);
				const attributes = { ...derivedAttributes };
				if (maxRule?.max > 0) {
					attributes.maxLength = maxRule.max;
				} else if (lengthRule?.length > 0) {
					attributes.maxLength = lengthRule.length;
				}
				setDerivedAttributes(attributes);
				break;
			}
			case "text-field": {
				setFieldValidationConfig(id, Yup.string(), validation);

				const maxRule = validation?.find((rule) => "max" in rule);
				const lengthRule = validation?.find((rule) => "length" in rule);
				const attributes = { ...derivedAttributes };
				if (maxRule?.max > 0) {
					attributes.maxLength = maxRule.max;
				} else if (lengthRule?.length > 0) {
					attributes.maxLength = lengthRule.length;
				}
				setDerivedAttributes(attributes);
				break;
			}
			default:
				break;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [validation]);

	useEffect(() => {
		if (value !== stateValue) {
			setStateValue(value ?? "");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

	useEffect(() => {
		if (
			uiType === "text-field" &&
			document?.activeElement === ref.current &&
			ref.current.selectionEnd !== caret.current
		) {
			// keep caret in place after uppercase, not available for 'email' & 'number' HTML input types
			ref.current.setSelectionRange(caret.current, caret.current);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stateValue]);

	useEffect(() => {
		if (schema.uiType === "text-field" && schema.customOptions?.textTransform === "uppercase" && stateValue) {
			setStateValue((previous) => previous?.toString().toUpperCase());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [schema.uiType === "text-field" && schema.customOptions?.textTransform]);

	// =============================================================================
	// EVENT HANDLERS
	// =============================================================================
	const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
		if (uiType === "numeric-field") {
			// truncate value to max decimal places instead of rejecting, so prefilled/pasted
			// values beyond the limit can still be edited
			const decimalsRule = (validation as INumericFieldValidationRule[])?.find((rule) => "decimals" in rule);
			if (decimalsRule?.decimals !== undefined) {
				const [intPart, decimalPart] = event.target.value.split(".");
				if (decimalPart && decimalPart.length > decimalsRule.decimals) {
					event.target.value = `${intPart}.${decimalPart.slice(0, decimalsRule.decimals)}`;
				}
			}
			const isNumber = !isNaN(parseFloat(event.target.value));
			const valueToUpdate = isNumber ? +event.target.value : undefined;
			onChange({ target: { value: valueToUpdate } });
			setStateValue(valueToUpdate ?? "");
			return;
		}

		if (schema.uiType === "text-field") {
			caret.current = event.target.selectionEnd; // must save current caret position before mutating event.target

			if (schema.customOptions?.textTransform === "uppercase") {
				event.target.value = event.target.value.toUpperCase();
			}

			onChange(event);
		} else {
			onChange(event);
		}

		setStateValue(event.target.value);
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
		if (uiType === "numeric-field") {
			// handle suppressing exponent input for numeric fields
			if (event.key.toLowerCase() === "e") {
				event.preventDefault();
			}
		}
	};

	const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>): void => {
		const pastedText = event.clipboardData.getData("text");
		// disable pasting of numbers with exponential notation for numeric fields
		if (customOptions?.preventCopyAndPaste || (uiType === "numeric-field" && /e/i.test(pastedText))) {
			event.preventDefault();
		}
	};

	// =============================================================================
	// HELPER FUNCTIONS
	// =============================================================================
	const formatInputMode = (): React.HTMLAttributes<HTMLInputElement>["inputMode"] => {
		if (inputMode) return inputMode;

		switch (uiType) {
			case "numeric-field":
				return "numeric";
			case "email-field":
				return "email";
			case "text-field":
				return "text";
			default:
				return "none";
		}
	};

	const formatInputType = (): HTMLInputTypeAttribute => {
		switch (uiType) {
			case "numeric-field":
				return "number";
			case "email-field":
				return "email";
			case "text-field":
			default:
				return "text";
		}
	};

	const hasAddOn = () => {
		return !!customOptions?.addOn;
	};

	const buildAddOn = (): AddonProps<undefined, undefined> => {
		if (!hasAddOn()) {
			return undefined;
		}

		if (customOptions.addOn.type === "label") {
			return {
				type: "label",
				position: customOptions.addOn.position,
				attributes: {
					value: customOptions.addOn.value,
				},
			};
		}

		if (customOptions.addOn.type === "icon") {
			const { icon, position, color } = customOptions.addOn;
			const Element = Icons[icon];
			return {
				type: "custom",
				position: position,
				attributes: {
					children: <CustomIcon as={Element} $color={color} />,
				},
			};
		}
	};

	// =============================================================================
	// RENDER FUNCTIONS
	// =============================================================================
	const InputElement = hasAddOn() ? Form.InputGroup : Form.Input;

	return (
		<>
			<InputElement
				{...inputProps}
				{...derivedAttributes}
				id={id}
				data-testid={TestHelper.generateId(id, uiType)}
				ref={ref}
				type={formatInputType()}
				label={formattedLabel}
				onBlur={onBlur}
				onPaste={handlePaste}
				onDrop={(e) => (customOptions?.preventDragAndDrop ? e.preventDefault() : null)}
				inputMode={formatInputMode()}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
				value={stateValue}
				errorMessage={error?.message}
				addon={buildAddOn()}
			/>
			<Warning id={id} message={warning} />
		</>
	);
};

interface CustomIconStyleProps {
	$color?: string;
}

const CustomIcon = styled.div<CustomIconStyleProps>`
	${({ $color }) => $color && `color: ${$color};`}
`;
